/**
 * Represents a feature hierarchy in the application.
 *
 * @interface Feature
 */
export interface Feature {
    /**
     * Unique identifier for the feature.
     */
    id: string;

    /**
     * Display name of the feature.
     */
    name: string;

    /**
     * UI-related metadata for the feature.
     */
    ui: {
        /**
         * Hierarchy information for menus.
         */
        hierarchy: {
            /**
             * Name of the main menu this feature belongs to.
             */
            mainMenu?: string;

            /**
             * Name of the submenu this feature belongs to.
             */
            subMenu?: string;

            /**
             * Level in the menu hierarchy.
             */
            level?: number;

            /**
             * Order in the menu or submenu.
             */
            order?: number;
        };

        /**
         * Icon information for different UI contexts.
         */
        icons: {
            /**
             * Icon used in menus.
             */
            menu?: string;

            /**
             * Icon used for group representation.
             */
            group?: string;

            /**
             * Main icon for the feature.
             */
            main?: string;
        };

        /**
         * Routing information for the feature.
         */
        route: {
            /**
             * Path for the route.
             */
            path: string;

            /**
             * List of route parameters.
             */
            params: string[];

            /**
             * List of query parameters for the route.
             */
            queryParams: string[];
        };
    };
}


/**
 * Enum representing the different roles in the application.
 *
 * @enum {string}
 */
export enum Roles {
    /**
     * Regular employee role.
     */
    EMPLOYEE = "EMPLOYEE",

    /**
     * Farm Business administrator with full access to business operations.
     */
    FARM_BUSINESS_ADMIN = "Farm Business Admin",

    /**
     * Owner of a farm.
     */
    FARMER = "Farmer",

    /**
     * Manager responsible for farm operations.
     */
    FARM_MANAGER = "Farm Manager",

    /**
     * Provider Business administrator with full access to business operations.
     */
    PROVIDER_BUSINESS_ADMIN = "Provider Business Admin",

    /**
     * Manager responsible for Provider business operations.
     */
    PROVIDER_MANAGER = "Provider Manager",

    /**
     * Customer support role, handling customer queries and support.
     */
    CUSTOMER_SUPPORT = "Customer Support",
}

/**
 * Maps each role to the list of features accessible by that role.
 *
 * @type {Record<Roles, Feature[]>}
 */
export const roleToFeatures: Record<Roles, Feature[]> = {
    [Roles.EMPLOYEE]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ROUTES",
            "name": "Routes",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/routes",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEATURES",
            "name": "Feature",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/feature",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SCREEN",
            "name": "Screen",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/screen",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SYNC",
            "name": "Sync",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/aclsync",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BUSINESS_CATEGORY",
            "name": "Business Category",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/businesscategory",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BUSINESS_TYPE",
            "name": "Business Type",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/businesstype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PERSON_ROLE",
            "name": "Person Role",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "User Setting",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "user-setting-icon.svg"
                },
                "route": {
                    "path": "/home/personrole",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRANSACTION_TYPE",
            "name": "Transaction Type",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/transactiontype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "UOM",
            "name": "UOM",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/uom",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SHAPES",
            "name": "Shapes",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/shapes",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ITEM_TYPE",
            "name": "Item Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/itemtype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DEVICE_MODEL",
            "name": "Device Model",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Hardware",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "hardware-icon.svg"
                },
                "route": {
                    "path": "/home/devicemodel",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SENSOR",
            "name": "Sensors",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Hardware",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "hardware-icon.svg"
                },
                "route": {
                    "path": "/home/sensor",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SENSOR_TYPE",
            "name": "Sensor Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Hardware",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "hardware-icon.svg"
                },
                "route": {
                    "path": "/home/sensortype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUALITY_PARAMETERS_TYPES",
            "name": "Quality Parameters Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Test Configurations",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "test-configuration-icon.svg"
                },
                "route": {
                    "path": "/home/qualityparamtype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUALITY_PARAMETERS",
            "name": "Quality Parameters",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Test Configurations",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "test-configuration-icon.svg"
                },
                "route": {
                    "path": "/home/qualityparameter",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TEST_CATEGORIES",
            "name": "Test Categories",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Test Configurations",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "test-configuration-icon.svg"
                },
                "route": {
                    "path": "/home/testcategory",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QP_RANGES",
            "name": "QP Ranges",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Test Configurations",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "test-configuration-icon.svg"
                },
                "route": {
                    "path": "/home/qprange",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTIVITIES_LIST",
            "name": "Activity Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/activitieslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BROOD_BREEDERS",
            "name": "Brood Breeders",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/broodbreederslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BROOD_LINES",
            "name": "Brood Lines",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/broodlineslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "OBSERVATION_TYPES",
            "name": "Observation Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/observationtypeslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TREATMENT_TYPES",
            "name": "Treatment Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/treatmenttypeslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_TYPES",
            "name": "Pond Types",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/pondtypeslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_USAGES",
            "name": "Pond Usages",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/pondusageslist",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRODUCTS",
            "name": "Products",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/products",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VENDORS",
            "name": "Vendor Master",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/vendormaster",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SPEND_ANALYSIS",
            "name": "Farm Spend Analysis",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/farmspendanalysis",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_TRANSACTIONS",
            "name": "Material Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/materialtransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENT_TRANSACTIONS",
            "name": "Payment Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Material",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "material-management-icon.svg"
                },
                "route": {
                    "path": "/home/paymenttransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_CATEGORIES",
            "name": "Culture Categories",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/culturecategory",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_TYPE",
            "name": "Water Type",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/watertype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SOIL_TYPE",
            "name": "Soil Type",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/soiltype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SPECIES_TYPE",
            "name": "Species Type",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Aquaculture",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "aqua-culture-icon.svg"
                },
                "route": {
                    "path": "/home/speciestype",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "QUICK_REGISTRATION",
            "name": "Quick Registration",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Business",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "trading-community-icon.svg"
                },
                "route": {
                    "path": "/home/Quick-Registartion",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BUSINESS_ENTITIES",
            "name": "Business Entities",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Business",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "trading-community-icon.svg"
                },
                "route": {
                    "path": "/home/Business-Entity/Business",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PEOPLE",
            "name": "People",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Business",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "trading-community-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/People",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BARCODES",
            "name": "Barcodes",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "barcode-icon.svg"
                },
                "route": {
                    "path": "/home/addbarcode",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TANK_SHIFT",
            "name": "Tank Shift",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "tankshift-icon.svg"
                },
                "route": {
                    "path": "/home/tankshift",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTUAL_VS_PROJECTED",
            "name": "Actual vs Projected",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/projectedfeedgraph",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WEEKLY_GROWTH",
            "name": "Weekly Growth",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/weeklygrowth",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_MOVEMENT",
            "name": "Material Movement Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/mmregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRANSACTIONS",
            "name": "Transactions Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/transactionregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENTS",
            "name": "Payments Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/paymentregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTIVITY_TRACKER_LEADS",
            "name": "Activity Tracker/Leads",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Staff",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/activitytracker",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRICE_TICKER_UPLOAD",
            "name": "Price Ticker Upload",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 5
                },
                "icons": {
                    "main": "price-ticker-icon.svg",
                },
                "route": {
                    "path": "/home/pricetickerupload",
                    "params": [],
                    "queryParams": []
                }
            }
        },
    ],
    [Roles.FARM_BUSINESS_ADMIN]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRODUCTS",
            "name": "Products",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/products",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VENDORS",
            "name": "Vendors",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/vendormaster",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SPEND_ANALYSIS",
            "name": "Farm Spend Analysis",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/farmspendanalysis",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_TRANSACTIONS",
            "name": "Material Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/materialtransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BATCH_TRANSACTIONS",
            "name": "Batch Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/batchentrydashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENT_TRANSACTIONS",
            "name": "Payment Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/paymenttransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_BUSINESS",
            "name": "Your Business",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-business-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarms",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_FIELD_FORCE",
            "name": "Your Field Force",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-filed-force-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/Staff",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TANK_SHIFT",
            "name": "Tank Shift",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "tankshift-icon.svg"
                },
                "route": {
                    "path": "/home/tankshift",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTUAL_VS_PROJECTED",
            "name": "Actual vs Projected",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/projectedfeedgraph",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WEEKLY_GROWTH",
            "name": "Weekly Growth",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/weeklygrowth",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_MOVEMENT",
            "name": "Material Movement Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/mmregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRANSACTIONS",
            "name": "Transactions Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/transactionregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENTS",
            "name": "Payments Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/paymentregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SUBSCRIPTION_PLANS",
            "name": "Subscription Plans",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 5
                },
                "icons": {
                    "main": "Dashboard.svg",
                },
                "route": {
                    "path": "/home/subscriptionPlans",
                    "params": [],
                    "queryParams": []
                }
            }
        }
    ],
    [Roles.FARMER]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRODUCTS",
            "name": "Products",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/products",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VENDORS",
            "name": "Vendors",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/vendormaster",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SPEND_ANALYSIS",
            "name": "Farm Spend Analysis",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/farmspendanalysis",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_TRANSACTIONS",
            "name": "Material Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/materialtransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BATCH_TRANSACTIONS",
            "name": "Batch Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/batchentrydashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENT_TRANSACTIONS",
            "name": "Payment Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/paymenttransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_BUSINESS",
            "name": "Your Business",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-business-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarms",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_FIELD_FORCE",
            "name": "Your Field Force",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-filed-force-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/Staff",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TANK_SHIFT",
            "name": "Tank Shift",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "tankshift-icon.svg"
                },
                "route": {
                    "path": "/home/tankshift",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTUAL_VS_PROJECTED",
            "name": "Actual vs Projected",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/projectedfeedgraph",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WEEKLY_GROWTH",
            "name": "Weekly Growth",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/weeklygrowth",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_MOVEMENT",
            "name": "Material Movement Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/mmregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRANSACTIONS",
            "name": "Transactions Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/transactionregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENTS",
            "name": "Payments Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/paymentregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SUBSCRIPTION_PLANS",
            "name": "Subscription Plans",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 5
                },
                "icons": {
                    "main": "Dashboard.svg",
                },
                "route": {
                    "path": "/home/subscriptionPlans",
                    "params": [],
                    "queryParams": []
                }
            }
        }
    ],
    [Roles.FARM_MANAGER]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 1
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRODUCTS",
            "name": "Products",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/products",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VENDORS",
            "name": "Vendors",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/vendormaster",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SPEND_ANALYSIS",
            "name": "Farm Spend Analysis",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/farmspendanalysis",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_TRANSACTIONS",
            "name": "Material Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/materialtransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "BATCH_TRANSACTIONS",
            "name": "Batch Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/batchentrydashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENT_TRANSACTIONS",
            "name": "Payment Transactions",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": "Inventory Control",
                    "level": 2,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "group": "Inventory Control.svg"
                },
                "route": {
                    "path": "/home/paymenttransaction",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_BUSINESS",
            "name": "Your Business",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-business-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarms",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_FIELD_FORCE",
            "name": "Your Field Force",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-filed-force-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/Staff",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TANK_SHIFT",
            "name": "Tank Shift",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 2
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "tankshift-icon.svg"
                },
                "route": {
                    "path": "/home/tankshift",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTUAL_VS_PROJECTED",
            "name": "Actual vs Projected",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/projectedfeedgraph",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ANALYTICS",
            "name": "Analytics",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Charts",
                    "subMenu": "Trend Analysis",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Charts.svg",
                },
                "route": {
                    "path": "/home/analytics",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WEEKLY_GROWTH",
            "name": "Weekly Growth",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/weeklygrowth",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "MATERIAL_MOVEMENT",
            "name": "Material Movement Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/mmregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRANSACTIONS",
            "name": "Transactions Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/transactionregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PAYMENTS",
            "name": "Payments Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 4
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/paymentregister",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SUBSCRIPTION_PLANS",
            "name": "Subscription Plans",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 5
                },
                "icons": {
                    "main": "Dashboard.svg",
                },
                "route": {
                    "path": "/home/subscriptionPlans",
                    "params": [],
                    "queryParams": []
                }
            }
        }
    ],
    [Roles.PROVIDER_BUSINESS_ADMIN]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home/dashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_CUSTOMERS",
            "name": "Your Customers",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 1
                },
                "icons": {
                    "main": "your-customer-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarmsProvider",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FARM_USERS",
            "name": "Farm Users",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 2
                },
                "icons": {
                    "main": "farm-user-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/People",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_BUSINESS",
            "name": "Your Business",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-business-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarmsProvider",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_FIELD_FORCE",
            "name": "Your Field Force",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-filed-force-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/Staff",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "PRODUCTS",
            "name": "Products",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "product-icon.svg"
                },
                "route": {
                    "path": "/home/products",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ORDERS",
            "name": "Orders",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "orders-icon.svg"
                },
                "route": {
                    "path": "/home/orders",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTIVITY_TRACKER_LEADS",
            "name": "Activity Tracker/Leads",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Staff",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/activitytracker",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VISITS",
            "name": "Visits",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Staff",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/visits",
                    "params": [],
                    "queryParams": []
                }
            }
        },

    ],
    [Roles.PROVIDER_MANAGER]: [
        {
            "id": "DASHBOARD",
            "name": "Dashboard",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "Dashboard.svg"
                },
                "route": {
                    "path": "/home/dashboard",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_CUSTOMERS",
            "name": "Your Customers",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 1
                },
                "icons": {
                    "main": "your-customer-icon.svg"
                },
                "route": {
                    "path": "/home/BusinessEntityFarmsProvider",
                    "params": ["businessID", "isAdmin"],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FARM_USERS",
            "name": "Farm Users",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 2
                },
                "icons": {
                    "main": "farm-user-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/People",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND",
            "name": "Pond",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmpond",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "FEED",
            "name": "Feed",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmfeed",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DENSITY",
            "name": "Density",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmstockingdensity",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "HARVEST",
            "name": "Harvest",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmharvest",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "WATER_QUALITY",
            "name": "Water Quality",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmwaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "SAMPLING",
            "name": "Sampling",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/farmsampling",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "READINGS_HISTORY",
            "name": "Water Quality Readings History",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Metrics",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 3
                },
                "icons": {
                    "menu": "Metrics.svg"
                },
                "route": {
                    "path": "/home/historywaterquality",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "YOUR_FIELD_FORCE",
            "name": "Your Field Force",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "your-filed-force-icon.svg"
                },
                "route": {
                    "path": "/home/Persons-List/Staff",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ORDERS",
            "name": "Orders",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Manage",
                    "subMenu": null,
                    "level": 1,
                    "order": 4
                },
                "icons": {
                    "menu": "manage-icon.svg",
                    "main": "orders-icon.svg"
                },
                "route": {
                    "path": "/home/orders",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "CULTURE_SUMMARY",
            "name": "Culture Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/culturesummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "POND_SUMMARY",
            "name": "Pond Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/pondsummaryreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "DAY_SUMMARY",
            "name": "Day Summary",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/daysummary",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "TRACEABILITY",
            "name": "Traceability Report",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Farm",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/traceabilityreport",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "ACTIVITY_TRACKER_LEADS",
            "name": "Activity Tracker/Leads",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Staff",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/activitytracker",
                    "params": [],
                    "queryParams": []
                }
            }
        },
        {
            "id": "VISITS",
            "name": "Visits",
            "ui": {
                "hierarchy": {
                    "mainMenu": "Reports",
                    "subMenu": "Staff",
                    "level": 2,
                    "order": 5
                },
                "icons": {
                    "menu": "Reports.svg",
                },
                "route": {
                    "path": "/home/visits",
                    "params": [],
                    "queryParams": []
                }
            }
        },

    ],
    [Roles.CUSTOMER_SUPPORT]: [
        {
            "id": "MARKET_CONNECT",
            "name": "Market Connect",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 0
                },
                "icons": {
                    "main": "marketconnect.svg"
                },
                "route": {
                    "path": "/home/marketconnect",
                    "params": [],
                    "queryParams": []
                }
            },


        },
        {
            "id": "SUBSCRIPTIONS",
            "name": "Subscriptions",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 1
                },
                "icons": {
                    "main": "marketconnect.svg"
                },
                "route": {
                    "path": "/home/subscriptions",
                    "params": [],
                    "queryParams": []
                }
            },
        },
        {
            "id": "BLUEYE",
            "name": "BluEye",
            "ui": {
                "hierarchy": {
                    "mainMenu": null,
                    "subMenu": null,
                    "level": 0,
                    "order": 2
                },
                "icons": {
                    "main": "Blue Eye logo without bg.png"
                },
                "route": {
                    "path": "/home/bluEye",
                    "params": [],
                    "queryParams": []
                }
            },


        }
    ]
}

/**
 * Returns the list of features available for a given role.
 *
 * @param role - The role whose features you want to fetch
 * @returns An array of Feature objects (empty array if role is not found)
 */
export function getFeaturesWithHierarchyByRole(role): Feature[] {
    return roleToFeatures[role] ?? [];
}
